"use client";

import type { ReactNode } from "react";
import { useAuth } from "@clerk/nextjs";
import { FEATURE_FLAGS, useFeatureFlag } from "@/lib/analytics/featureFlags";

/**
 * Decides only whether the Facts inspector is VISIBLE. It is not the data
 * boundary: GET /api/onboarding/facts/debug enforces sign-in and serves only
 * the caller's own onboarding session, whatever this gate decides.
 *
 * The flag hook lives in the inner component so it mounts only for signed-in
 * viewers; anonymous visitors (and visitors while Clerk loads) never evaluate
 * the flag. The gate fails closed: an unknown or loading verdict renders
 * nothing. `?flags=facts` reaches it through useFeatureFlag's override path.
 */
export function FactsInspectorGate({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();
  if (!isLoaded || !isSignedIn) return null;
  return <SignedInFactsInspectorGate>{children}</SignedInFactsInspectorGate>;
}

function SignedInFactsInspectorGate({ children }: { children: ReactNode }) {
  const enabled = useFeatureFlag(FEATURE_FLAGS.FACTS_DEBUG_INSPECTOR);
  return enabled ? <>{children}</> : null;
}
